import { useForm } from "src/api/hooks/useForm";
import { lang } from "src/language/Strings";
import { useParams, useLocation } from "react-router-dom";
import { useProjectVariationById } from "src/api/hooks/useProjectVariationById";
import useUsers from "src/api/hooks/useUsers";
import useAssignmentMutation from "src/api/hooks/useAssignmentMutation";
import { useAssignmentByInspectionId } from "src/api/hooks/useAssignment";
import { useEffect, useState } from "react";
import { routes } from "src/routes";
import { ItemDescription } from "./ItemDescription";
import { Modal } from "./Modal";
import { InspectionResponse } from "src/types/InspectionUpdateRequest";

export default function AddAssignmentModal({
  isAddModalOpen,
  setIsAddModalOpen,
  editAssignmentId,
  setEditAssignmentId,
  inspectionId,
  inspection,
}: {
  isAddModalOpen: boolean;
  setIsAddModalOpen: (isAddModalOpen: boolean) => void;
  editAssignmentId?: number;
  setEditAssignmentId: (editAssignmentId: number | undefined) => void;
  inspectionId: string;
  inspection?: InspectionResponse;
}) {
  const { companyId } = useParams();
  const { data: companyUsers } = useUsers();
  const assignments = useAssignmentByInspectionId();
  const location = useLocation();

  const { Form, formData, setFormData, resetForm } = useForm({
    title: inspection?.title,
    description: inspection?.description,
    companyId: companyId,
    assignedToGroupId: inspection?.groupId,
    assignedToUserId: inspection?.userId,
    deviationId: inspectionId,
    status: 1,
    startTime: new Date(),
    endTime: new Date(new Date().setDate(new Date().getDate() + 7)),
    dueDate: new Date(new Date().setDate(new Date().getDate() + 7)),
    assignmentType: location.pathname.startsWith(
      routes.Egenkontroller.Pages.List.url(companyId)
    )
      ? "Egenkontroller"
      : "Deviation",
  });

  useEffect(() => {
    if (editAssignmentId && assignments.data) {
      const assignment = assignments.data.find(
        (assignment) => assignment.id === editAssignmentId
      );
      if (assignment) {
        setFormData((prev) => ({
          ...prev,
          assignedToGroupId: assignment.assignedToGroupId,
          assignedToUserId: assignment.assignedToUserId,
          dueDate: assignment.dueDate
            ? new Date(assignment.dueDate)
            : new Date(),
          startTime: assignment.startTime
            ? new Date(assignment.startTime)
            : new Date(),
          endTime: assignment.endTime
            ? new Date(assignment.endTime)
            : new Date(),
          status: assignment.status,
          title: assignment.title,
          description: assignment.description,
          deviationId: assignment.deviationId,
          assignmentType: assignment.assignmentType,
          id: assignment.id,
        }));
      }
    }
  }, [editAssignmentId, assignments.data]);

  const mutation = useAssignmentMutation();
  const { invalidate } = useAssignmentByInspectionId();
  const [rangeOrDueDate, setRangeOrDueDate] = useState<string>("range");

  if (!isAddModalOpen) return null;
  return (
    <Modal
      onOutsideClick={() => setIsAddModalOpen(false)}
      className={!inspection ? "hidden" : ""}
    >
      <div className="bg-White rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {editAssignmentId ? lang.editAssignment : lang.addAssignment}
        </h2>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate(formData);
            invalidate();
            setIsAddModalOpen(false);
            setEditAssignmentId(undefined);
            resetForm();
          }}
        >
          <ItemDescription label={lang.title}>
            <Form.Input
              name="title"
              type="text"
              defaultValue={formData.title}
            />
          </ItemDescription>
          <ItemDescription label={lang.description}>
            <Form.TextArea
              name="description"
              rows={6}
              className="w-full text-sm"
              defaultValue={formData.description}
            />
          </ItemDescription>
          <div className="flex gap-2 justify-between">
            <input
              type="radio"
              name="rangeOrDueDate"
              defaultChecked={rangeOrDueDate === "range"}
              value="range"
              onChange={() => setRangeOrDueDate("range")}
            />
            <label htmlFor="range">{lang.range}</label>
            <input
              type="radio"
              name="rangeOrDueDate"
              defaultChecked={rangeOrDueDate === "dueDate"}
              value="dueDate"
              onChange={() => setRangeOrDueDate("dueDate")}
            />
            <label htmlFor="dueDate">{lang.dueDate}</label>
          </div>
          {rangeOrDueDate === "range" ? (
            <>
              <ItemDescription label={lang.startDate} className="text-sm mt-4">
                <Form.DateInput
                  name="startTime"
                  defaultValue={formData.startTime}
                />
              </ItemDescription>
              <ItemDescription label={lang.endDate} className="text-sm">
                <Form.DateInput
                  name="endTime"
                  defaultValue={formData.endTime || formData.startTime}
                />
              </ItemDescription>
            </>
          ) : (
            <ItemDescription label={lang.dueDate} className="text-sm mt-4">
              <Form.DateInput name="dueDate" defaultValue={formData.dueDate} />
            </ItemDescription>
          )}
          <ItemDescription label={lang.assignedTo}>
            <Form.Select
              name="assignedToUserId"
              defaultValue={String(formData.assignedToUserId)}
            >
              {companyUsers.map((user) => (
                <Form.Select.Option key={String(user.id)} value={user.id}>
                  {user.firstName + " " + user.surname}
                </Form.Select.Option>
              ))}
            </Form.Select>
          </ItemDescription>
        </Form>
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={() => {
              setIsAddModalOpen(false);
              setEditAssignmentId(undefined);
            }}
            className="px-4 py-2 bg-PrimaryGreen text-White rounded-lg   hover:text-Grey4"
          >
            {lang.cancel}
          </button>
          <button
            onClick={() => {
              mutation.mutate({
                ...formData,
                dueDate:
                  rangeOrDueDate === "dueDate"
                    ? formData.dueDate?.toLocaleDateString()
                    : undefined,
                startTime:
                  rangeOrDueDate === "range"
                    ? formData.startTime?.toLocaleDateString()
                    : undefined,
                endTime:
                  rangeOrDueDate === "range"
                    ? formData.endTime?.toLocaleDateString()
                    : undefined,
              });
              invalidate();
              setIsAddModalOpen(false);
              setEditAssignmentId(undefined);
              resetForm();
            }}
            className="px-4 py-2 bg-PrimaryGreen text-White text-sm rounded-lg hover:bg-PrimaryGreen/80"
          >
            {lang.save}
          </button>
        </div>
      </div>
    </Modal>
  );
}
